import React, { useEffect, useState } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormLabel from "components/Extra/FormLabel"
import { useTranslation } from "react-i18next"

interface AddNewDeliveryManProps {
  isOpen: boolean
  toggle: () => void
}

export const AddDeliveryMan: React.FC<AddNewDeliveryManProps> = ({
  isOpen,
  toggle,
}) => {
  const { t } = useTranslation()

  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("0")

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      deliveryManName: "",
      mobileNumber: "",
      address: "",
      deliveryPartnerOverview: "",
      active: true,
    },
    validationSchema: Yup.object({
      deliveryManName: Yup.string()
        .trim()
        .required(t("deliveryMen_deliveryManNameValidationMessage") as string),
      mobileNumber: Yup.string()
        .trim()
        .required(t("deliveryMen_mobileNumberValidationMessage") as string),
      address: Yup.string()
        .trim()
        .required(t("deliveryMen_addressValidationMessage") as string),
      deliveryPartnerOverview: Yup.string().trim(),
      visibility: Yup.boolean(),
    }),
    onSubmit: values => {
      const {
        active,
        address,
        deliveryManName,
        deliveryPartnerOverview,
        mobileNumber,
      } = values
      console.log(values)

      // TODO: Dispatch
      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    validation.resetForm()
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24">
          {t("deliveryMen_addDeliveryMan")}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Accordion open={openFirstAccordion}>
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                }
              >
                {t("common_basicInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormGroup>
                  <FormLabel htmlFor="deliveryManName" required>
                    {t("deliveryMen_deliveryManName")}
                  </FormLabel>

                  <Input
                    id="deliveryManName"
                    name="deliveryManName"
                    type="text"
                    placeholder={t("deliveryMen_enterDeliveryName") as string}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.deliveryManName || ""}
                    invalid={
                      validation.touched.deliveryManName &&
                      validation.errors.deliveryManName
                        ? true
                        : false
                    }
                  />
                  {validation.touched.deliveryManName &&
                  validation.errors.deliveryManName ? (
                    <FormFeedback type="invalid">
                      {validation.errors.deliveryManName}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="mobileNumber" required>
                    {t("deliveryMen_mobileNumber")}
                  </FormLabel>
                  <Input
                    id="mobileNumber"
                    name="mobileNumber"
                    type="number"
                    placeholder={t("deliveryMen_enterMobile") as string}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.mobileNumber || ""}
                    invalid={
                      validation.touched.mobileNumber &&
                      validation.errors.mobileNumber
                        ? true
                        : false
                    }
                  />
                  {validation.touched.mobileNumber &&
                  validation.errors.mobileNumber ? (
                    <FormFeedback type="invalid">
                      {validation.errors.mobileNumber}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="address" required>
                    {t("deliveryMen_address")}
                  </FormLabel>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    placeholder={
                      t("deliveryMen_enterDeliveryAddress") as string
                    }
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.address || ""}
                    invalid={
                      validation.touched.address && validation.errors.address
                        ? true
                        : false
                    }
                  />
                  {validation.touched.address && validation.errors.address ? (
                    <FormFeedback type="invalid">
                      {validation.errors.address}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="address">
                    {t("deliveryMen_deliveryPartnerOverview")}
                  </FormLabel>
                  <Input
                    id="address"
                    name="address"
                    type="textarea"
                    placeholder={
                      t("deliveryMen_enterDeliveryPartnerOverview") as string
                    }
                    rows={3}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.address || ""}
                    invalid={
                      validation.touched.address && validation.errors.address
                        ? true
                        : false
                    }
                  />
                  {validation.touched.address && validation.errors.address ? (
                    <FormFeedback type="invalid">
                      {validation.errors.address}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
          <Accordion open={openSecondAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenSecondAccordion(
                    openSecondAccordion === "0" ? "1" : "0"
                  )
                }
              >
                {t("deliveryMen_activeStatus")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormGroup>
                  <FormGroup className="d-flex align-items-center gap-3">
                    <FormLabel htmlFor="visibility">
                      <span className="fw-bold">{t("deliveryMen_active")}</span>{" "}
                      ({t("deliveryMen_deliveryManVisibility")})
                    </FormLabel>
                    <div className="form-check form-switch mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="visibility"
                        defaultChecked
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                      />
                    </div>
                  </FormGroup>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn btn-primary save-customer`}
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {t("common_create")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
